import type {Lifetime} from "../lifetime";
import {isArray} from "../../bootstrap/common/arrays";
import {GLOBAL, type GlobalWindow} from "../globals";
import {Page} from "../page";
import {elementFrom} from "./html";
import type {Maybe, Nullable} from "../../bootstrap/common/types";


export const ACTIVATION_CHANGE = "activationChange";

export function eopCustomEvent<DETAIL>(name: string, details?: DETAIL): CustomEvent<DETAIL> {
    return new CustomEvent(name, {bubbles: true, composed: true, detail: details});
}

declare global {
    interface CustomEventMap {
        "activationChange": CustomEvent<ActivationChangeEventParams>;
    }
}

export class ActivationChangeEventParams {
    public constructor(
        public transition: "init" | "hide" | "show" | "animate",
        private parent: Element,
        private scopeClass?: string) {
    }

    public affects(element: Element): boolean {
        if (element === this.parent) {
            return true;
        }
        const scope = element.parentElement?.closestThat(e =>
            (this.scopeClass && e.classList.contains(this.scopeClass))
            || !(e instanceof HTMLElement)
            || e === this.parent);
        return scope === this.parent;
    }

}

export function clickEventAlreadyHandledByChild(event: Event, parent: Element): boolean {
    for (const node of event.composedPath()) {
        if (node === parent) {
            return false;
        }
        if (node instanceof HTMLElement && node.isVisible() && node.isClickable()) {
            return true;
        }
    }

    return false;
}

export function eventOccurredOn(event: Event, element: Element): boolean {
    return Array.from(event.composedPath()).first() === element;
}

export function eventOccurredInside(event: Event, element: Element): boolean {
    return event.composedPath()
        .filter(target => target instanceof Element)
        .some(target => target === element || (target).hasAncestor(element, {transparent: true}));
}

export function eventOccurredOutside(event: Event, element: Element): boolean {
    return !eventOccurredInside(event, element);
}

export function eventOccurredInsideAll(event: Event, ...elements: Element[]): boolean {
    return elements.some(element => eventOccurredInside(event, element));
}

export function eventOccurredOutsideAll(event: Event, ...elements: Element[]): boolean {
    return elements.every(element => eventOccurredOutside(event, element));
}

export function isMouseClick(event?: MouseEvent): boolean {
    return event?.detail !== undefined && event.detail > 0;
}

export function onTabPressAfter(element: Element, callback: () => void, nextTabElement?: HTMLElement): void;
export function onTabPressAfter(element: () => Nullable<Element>, callback: () => void, nextTabElement?: HTMLElement): void;
export function onTabPressAfter(element: any, callback: () => void, nextTabElement?: HTMLElement): void {
    const abortController = new AbortController();
    GLOBAL.document().addEventListener("keydown", (ev: KeyboardEvent) => {
        const activeElement = Page.activeElement();
        const triggerElement = typeof element === "function" ? element() : element;
        if (ev.key === KEYS.TAB && !ev.shiftKey && activeElement && triggerElement && activeElement === triggerElement) {
            if (nextTabElement) {
                ev.preventDefault();
                ev.stopPropagation();
                nextTabElement.focus();
            }
            callback();
            abortController.abort();
        }
    }, {capture: true, signal: abortController.signal});
}

export function focusElementOnNextTabPress(element: HTMLElement): void {
    const abortController = new AbortController();
    GLOBAL.document().addEventListener("keydown", (ev: KeyboardEvent) => {
        if (ev.key === KEYS.TAB && !ev.shiftKey) {
            ev.preventDefault();
            ev.stopPropagation();
            element.focus();
            abortController.abort();
        }
    }, {capture: true, signal: abortController.signal});
}

export function onTabbingOutOfDialog(element: Element, callback: () => void): void {
    let dummyFocusable = element.querySelector(".tab-out-button");
    if (dummyFocusable === null) {
        dummyFocusable = elementFrom(`<span class="tab-out-button" role="button" tabindex="0" aria-label="Tab-Out-Element"></span>`);
        element.append(dummyFocusable);
    }

    GLOBAL.document().addEventListener("focusin", (ev: FocusEvent) => {
        if (ev.target instanceof HTMLElement && ev.target === dummyFocusable) {
            ev.preventDefault();
            ev.stopPropagation();
            callback();
        }
    }, {capture: true});
}

export function handleActivationKey(ev: KeyboardEvent, callback: () => void): void {
    if (ev.key === KEYS.ENTER || ev.key === KEYS.SPACE) {
        ev.preventDefault();
        ev.stopPropagation();
        callback();
    }
}

export function installEscapeHandlerOn(element: Lifetime, callback: (event: Event) => void): void {
    prepareEvents(GLOBAL.document())
        .boundTo(element)
        .on("keydown", (ev) => escapeKeyHandler(ev, callback));
}

function escapeKeyHandler(evt: KeyboardEvent, callback: (event: Event) => void): void {
    if (evt.key === KEYS.ESCAPE) {
        evt.stopPropagation();
        callback(evt);
    }
}


export const KEYS = {
    ENTER: "Enter",
    ESCAPE: "Escape",
    TAB: "Tab",
    ARROW_UP: "ArrowUp",
    ARROW_DOWN: "ArrowDown",
    ARROW_LEFT: "ArrowLeft",
    ARROW_RIGHT: "ArrowRight",
    SPACE: " ",
    MEDIA_PLAY_PAUSE: "MediaPlayPause"
};

export const CLICK = {
    STOP_PROPAGATION: false,
    CONTINUE_PROPAGATION: true
};

type EventInstallable = GlobalWindow | Document | Element | Element[]

type EventTypeMap<T extends EventInstallable> = T extends GlobalWindow
    ? WindowEventMap
    : (T extends Document
        ? DocumentEventMap
        : (T extends Element
            ? ElementEventMap
            : (T extends Element[]
                ? ElementEventMap
                : never)));

export class EventInstaller<T extends EventInstallable> {
    private lifetime: Maybe<Lifetime>;

    public constructor(private element: T) {
    }

    public boundTo(lifetime: Lifetime): this {
        this.lifetime = lifetime;
        return this;
    }

    public on<K extends keyof EventTypeMap<T>>(event: K, listener: (ev: EventTypeMap<T>[K]) => any, options?: AddEventListenerOptions): this;
    public on<K extends keyof CustomEventMap>(event: K, listener: (ev: CustomEventMap[K]) => any, options?: AddEventListenerOptions): this;
    public on(event: string, listener: EventListenerOrEventListenerObject, options?: AddEventListenerOptions): this;
    public on(event: string, listener: EventListenerOrEventListenerObject, options?: AddEventListenerOptions): this {
        const signal = this.lifetime?.signal();
        let effectiveOptions = options;
        if (signal) {
            if (effectiveOptions) {
                effectiveOptions.signal = signal;
            } else {
                effectiveOptions = {signal};
            }
        }
        if (isArray(this.element)) {
            for (const element of (this.element as Element[])) {
                element.addEventListener(event, listener, effectiveOptions);
            }
        } else {
            this.element.addEventListener(event, listener, effectiveOptions);
        }
        return this;
    }
}

export function prepareEvents<T extends GlobalWindow | Document | Element | Element[]>(element: T): EventInstaller<T> {
    return new EventInstaller(element);
}

